/**
 * Admin POS-matching inspector (feat-115) — read-only diagnostic page.
 *
 * ADR 0001 §D9: a NEW dedicated admin-gated page (not an extension of
 * ShiftTranscriptView). Flow: location + date list picker → click a row →
 * order-centric detail. The five detail sections live in PosInspectorDetail.tsx
 * (rendered free of page chrome so they're unit-testable + restyleable).
 *
 * Gating: route is wrapped in <AdminRoute> (strict role === 'Admin', ADR §D2);
 * the tile is adminOnly in Administration.tsx. This component does not re-gate.
 *
 * NOTE: the VISUAL layout is functional-but-provisional pending design-lead's
 * approved mock (ADR §D9). Data flow, states, and linkage semantics are final.
 */
import React from 'react';
import ScorecardNavigation from '../ScorecardNavigation';
import {
  listOrdersForInspection,
  inspectOrder,
  listInspectorLocations,
  PosInspectorNotFoundError,
  PosInspectListRow,
  PosInspectorLocation,
  PosOrderInspectDetail,
} from '../../../services/posInspectorService';
import { OrderDetail, money, StateChip } from './PosInspectorDetail';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Searchable location dropdown (feat-120). Lists only locations that have POS orders
 * (from GET /api/admin/pos/inspector/locations) — location name primary, tenant name
 * subordinate. Dependency-free combobox: trigger → search field + filtered options.
 */
const LocationCombobox: React.FC<{ value: string; onChange: (id: string) => void }> = ({ value, onChange }) => {
  const [locations, setLocations] = React.useState<PosInspectorLocation[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let alive = true;
    listInspectorLocations()
      .then((l) => alive && setLocations(l))
      .catch((e) => alive && setLoadError(e instanceof Error ? e.message : 'Failed to load locations.'));
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = locations?.find((l) => l.location_id === value) ?? null;
  const q = query.trim().toLowerCase();
  const filtered = (locations ?? []).filter(
    (l) => !q || l.location_name.toLowerCase().includes(q) || l.tenant_name.toLowerCase().includes(q),
  );

  return (
    <div className="relative" ref={ref} data-testid="pos-inspector-location-combobox">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-softBorder rounded px-2 py-1.5 text-left flex items-center justify-between gap-2"
        data-testid="pos-inspector-location-trigger"
      >
        <span className={`truncate ${selected ? 'text-ink' : 'text-inkMuted'}`}>
          {selected
            ? selected.location_name
            : locations === null && !loadError
            ? 'Loading locations…'
            : 'Select a location…'}
        </span>
        <span className="text-inkMuted shrink-0">▾</span>
      </button>
      {loadError && <p className="mt-1 text-xs text-danger-text" data-testid="pos-inspector-location-error">{loadError}</p>}
      {open && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border border-softBorder rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col"
          data-testid="pos-inspector-location-menu"
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search locations…"
            className="m-2 px-2 py-1.5 border border-softBorder rounded text-sm shrink-0"
            data-testid="pos-inspector-location-search"
          />
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-inkMuted">
                {locations === null ? 'Loading…' : 'No matching locations.'}
              </p>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.location_id}
                  type="button"
                  onClick={() => {
                    onChange(l.location_id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-cream/70 ${l.location_id === value ? 'bg-coral-light/40' : ''}`}
                  data-testid={`pos-inspector-location-option-${l.location_id}`}
                >
                  <div className="text-sm text-ink">{l.location_name}</div>
                  <div className="text-xs text-inkMuted">{l.tenant_name}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PosInspector: React.FC = () => {
  // Picker state
  const [locationId, setLocationId] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState(isoDaysAgo(7));
  const [dateTo, setDateTo] = React.useState(isoDaysAgo(0));

  // Direct-lookup state (spec entry-model (a))
  const [lookupSource, setLookupSource] = React.useState('square');
  const [lookupVendorId, setLookupVendorId] = React.useState('');

  // List state
  const [listRows, setListRows] = React.useState<PosInspectListRow[] | null>(null);
  const [listCapped, setListCapped] = React.useState(false);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState<string | null>(null);

  // Detail state
  const [detail, setDetail] = React.useState<PosOrderInspectDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  const loadList = async () => {
    if (!locationId.trim()) {
      setListError('Enter a location ID first — the inspector is location-keyed (ADR §D1).');
      return;
    }
    setListLoading(true);
    setListError(null);
    try {
      const resp = await listOrdersForInspection({
        locationId: locationId.trim(),
        dateFrom,
        dateTo,
      });
      setListRows(resp.orders);
      setListCapped(resp.capped);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load orders.');
      setListRows(null);
    } finally {
      setListLoading(false);
    }
  };

  const loadDetail = async (source: string, vendorOrderId: string) => {
    if (!locationId.trim()) {
      setDetailError('Enter a location ID first.');
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const d = await inspectOrder(locationId.trim(), source, vendorOrderId);
      setDetail(d);
    } catch (e) {
      if (e instanceof PosInspectorNotFoundError) {
        setDetailError('Order not found, or not accessible at this location (fail-closed 404).');
      } else {
        setDetailError(e instanceof Error ? e.message : 'Failed to load order detail.');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  // List row → select + load into the detail pane (highlights the active row).
  const selectOrder = (source: string, vendorOrderId: string) => {
    setSelectedKey(`${source}:${vendorOrderId}`);
    loadDetail(source, vendorOrderId);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="pos-inspector">
      <ScorecardNavigation />
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <header className="mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-3xl font-display font-semibold text-ink">POS Matching Inspector</h1>
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-coral bg-coral-light/50 border border-coral/40 rounded-full px-2 py-0.5"
              data-testid="pos-inspector-internal-badge"
            >
              🔒 Bespoke internal · admin only
            </span>
          </div>
          <p className="text-sm text-inkMuted">
            Read-only diagnostic. Look up a POS transaction to see the full matching chain:
            order → covering shift → lead-up timeline → classified line items → the
            association/conversion analysis.
          </p>
        </header>

        {/* ----- Picker ----- */}
        <section className="bg-white border border-softBorder rounded-xl p-5 shadow-sm mb-6" data-testid="pos-inspector-picker">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <label className="text-sm">
              <span className="block text-inkMuted mb-1">Location</span>
              <LocationCombobox value={locationId} onChange={setLocationId} />
            </label>
            <label className="text-sm">
              <span className="block text-inkMuted mb-1">From</span>
              <input type="date" className="w-full border border-softBorder rounded px-2 py-1.5" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label className="text-sm">
              <span className="block text-inkMuted mb-1">To</span>
              <input type="date" className="w-full border border-softBorder rounded px-2 py-1.5" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            <div className="flex items-end">
              <button
                onClick={loadList}
                disabled={listLoading}
                className="w-full bg-coral text-white rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                data-testid="pos-inspector-load-list"
              >
                {listLoading ? 'Loading…' : 'Load orders'}
              </button>
            </div>
          </div>

          {/* Direct lookup (spec entry-model a) */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border-t border-softBorder pt-4">
            <label className="text-sm">
              <span className="block text-inkMuted mb-1">Source</span>
              <select className="w-full border border-softBorder rounded px-2 py-1.5" value={lookupSource} onChange={(e) => setLookupSource(e.target.value)}>
                <option value="square">square</option>
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block text-inkMuted mb-1">Vendor order ID</span>
              <input
                className="w-full border border-softBorder rounded px-2 py-1.5"
                value={lookupVendorId}
                onChange={(e) => setLookupVendorId(e.target.value)}
                placeholder="direct lookup by order id"
                data-testid="pos-inspector-vendor-id"
              />
            </label>
            <div className="flex items-end">
              <button
                onClick={() => { setSelectedKey(null); loadDetail(lookupSource, lookupVendorId.trim()); }}
                disabled={!lookupVendorId.trim() || detailLoading}
                className="w-full bg-ink text-white rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                data-testid="pos-inspector-lookup"
              >
                Inspect
              </button>
            </div>
          </div>
          {listError && <p className="mt-3 text-sm text-danger-text" data-testid="pos-inspector-list-error">{listError}</p>}
        </section>

        {/* ----- Results: two-pane master / detail ----- */}
        {(listRows || detail || detailLoading || detailError) && (
          <div className="lg:grid lg:grid-cols-[minmax(300px,360px)_1fr] lg:gap-6 lg:items-start">

            {/* LEFT: orders list — capped height, independently scrollable, sticky on wide screens */}
            {listRows && (
              <section
                className="bg-white border border-softBorder rounded-2xl shadow-sm overflow-hidden mb-6 lg:mb-0 flex flex-col max-h-[60vh] lg:max-h-[calc(100vh-2rem)] lg:sticky lg:top-4"
                data-testid="pos-inspector-list"
              >
                <div className="px-4 py-3 border-b border-softBorder bg-cream/40 shrink-0">
                  <div className="flex items-baseline justify-between">
                    <h2 className="text-base font-display font-semibold text-ink">Orders</h2>
                    <span className="text-xs text-inkMuted tabular-nums">{listRows.length}{listCapped ? '+' : ''}</span>
                  </div>
                  <div className="text-xs text-inkMuted mt-0.5">Click an order to inspect its matching chain.</div>
                </div>
                {listCapped && (
                  <div className="mx-3 mt-3 p-2 bg-preview-light border border-preview-border rounded text-xs text-preview shrink-0">
                    Showing the first results only (server limit reached) — narrow the date range to see all.
                  </div>
                )}
                <div className="overflow-y-auto flex-1 min-h-0 p-2 space-y-1">
                  {listRows.length === 0 ? (
                    <p className="text-sm text-inkMuted p-3">No POS orders in this location + date range.</p>
                  ) : (
                    listRows.map((r) => {
                      const key = `${r.source}:${r.vendor_order_id}`;
                      const active = key === selectedKey;
                      return (
                        <button
                          key={key}
                          onClick={() => selectOrder(r.source, r.vendor_order_id)}
                          data-testid={`pos-inspector-row-${r.vendor_order_id}`}
                          aria-current={active}
                          className={`w-full text-left rounded-lg px-3 py-2 border transition-colors ${active ? 'border-coral bg-coral-light/40' : 'border-transparent hover:bg-cream/70'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-xs text-ink truncate">{r.vendor_order_id}</span>
                            <StateChip state={r.state} />
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-1 text-xs text-inkMuted tabular-nums">
                            <span className="truncate">{r.source} · {r.opened_at_local}</span>
                            <span className="text-ink shrink-0">{money(r.final_total)}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-inkMuted tabular-nums">
                            <span title="covering shifts">🗓 {r.covering_shift_count}</span>
                            <span title="associations (upsell attempts)">↗ {r.association_count}</span>
                            <span title="conversions">✓ {r.conversion_count}</span>
                            {r.is_multi_snapshot && <span className="text-[10px]">· multi-snap</span>}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </section>
            )}

            {/* RIGHT: detail pane (scrolls with the page; the list stays put via sticky) */}
            <div className="min-w-0" data-testid="pos-inspector-detail-pane">
              {detailLoading && <p className="text-sm text-inkMuted p-4" data-testid="pos-inspector-detail-loading">Loading order…</p>}
              {detailError && <p className="text-sm text-danger-text p-4" data-testid="pos-inspector-detail-error">{detailError}</p>}
              {!detailLoading && !detailError && detail && <OrderDetail detail={detail} />}
              {!detailLoading && !detailError && !detail && (
                <div className="bg-white border border-dashed border-softBorder rounded-2xl p-10 text-center text-sm text-inkMuted" data-testid="pos-inspector-detail-empty">
                  {listRows
                    ? 'Select an order from the list to inspect its full matching chain.'
                    : 'Load orders for a location, or look up an order directly, to begin.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PosInspector;
